<template>
  <div tabindex="-1" @keyup="leftOrRightArrowPress" class="page page-with-tabs">
    <general-loader v-if="loading" loading/>
    <TabLayout v-else class="reservation-view" :tabs="tabs">
      <template v-slot:title>
        <artisio-breadcrumb :items="breadcrumbs"/>
      </template>
      <template v-slot:buttons>
        <b-dropdown v-if="!disableActions" class="reservation-actions-dropdown" right v-permission="'change_auction'"
                    :text="$t('More')">
          <b-dropdown-item @click="onEditClick">
            {{ $t('Edit Lot') }}
          </b-dropdown-item>
        </b-dropdown>
      </template>
      <template v-slot:right-placeholder>
        <template>
          <b-button :disabled="!previousUuid" @click="getPrevious" variant="outline-primary"
                    class="mb-0 mr-2 d-flex align-items-center">
            <font-awesome-icon icon="angle-double-left" class="icon mr-2"/>
            {{ $t('Previous Item') }}
          </b-button>
          <b-input type="text" :value="reservation.lot_no"
                   :class="!isLotNoInputFocused? 'text-center lot-no-input': 'lot-no-input'"
                   @keyup="onLotInputKeyUp" @blur="onLotInputBlur" @focus.native="onFocus"/>
          <b-button :disabled="!nextUuid" @click="getNext" variant="outline-primary"
                    class="mb-0 ml-2 d-flex align-items-center">
            {{ $t('Next Item') }}
            <font-awesome-icon icon="angle-double-right" class="icon ml-2"/>
          </b-button>
        </template>
      </template>
    </TabLayout>
  </div>
</template>

<script>
  import GeneralLoader from "@/core/components/general-loader/GeneralLoader";
  import TabLayout from "@/core/components/layout/DefaultLayout/TabLayout";
  import ArtisioBreadcrumb from "@/core/components/artisio-breadcrumb/ArtisioBreadcrumb";
  import {createNamespacedHelpers} from "vuex";
  import {SOURCE_RESERVATION_DASHBOARD} from "@/modules/constants";
  import InventoryService from "@/modules/inventory/InventoryService";

  const {mapActions, mapState, mapGetters} = createNamespacedHelpers('reservations');
  const {mapState: globalMapState} = createNamespacedHelpers('global');
  const {mapActions: lotsMapActions} = createNamespacedHelpers('lots');

  export default {
    name: "ReservationsView",
    components: {GeneralLoader, TabLayout, ArtisioBreadcrumb},
    data: () => {
      return {
        routeFrom: null,
        previousUuid: null,
        previousPage: null,
        nextUuid: null,
        nextPage: null,
        breadcrumbs: [],
        isLotNoInputFocused: false,
      };
    },
    computed: {
      ...mapState({
        loading: state => state.view.loading,
        reservation: state => state.view.currentReservation,
        inventory: state => state.view.currentReservation.inventory,
      }),
      ...globalMapState({
        enableContinuousAuction: state => state.enableContinuousAuction,
      }),
      ...mapGetters({
        disableActions: 'disableActions'
      }),
      tabs() {
        let tabs = [
          {title: 'Dashboard', icon: 'server', to: {name: 'reservations.dashboard'}},
        ];

        // Removing settings tab when lot is not present cause whole page is empty
        if (this.enableContinuousAuction || this.reservation.lot) {
          tabs = [
            ...tabs,
            ...[
              {title: 'Settings', icon: 'cog', to: {name: 'reservations.settings'}},
              {title: 'Bids', icon: 'stream', to: {name: 'reservations.bids'}},
              {title: 'Max Bids', icon: 'gavel', to: {name: 'reservations.pre-bids'}},
            ]
          ]
        }

        tabs.push({title: 'Notes', icon: 'comment-alt', to: {name: 'reservations.notes'}})

        return tabs;
      }
    },
    watch: {
      async '$route.params.id'() {
        await this.getReservation(this.$route.params.id);
        this.calculateInfo();
      },
      reservation() {
        this.initBreadcrumbs();
      }
    },
    methods: {
      ...mapActions(['getReservation', 'getReservationByLotNo']),
      ...lotsMapActions({
        showReservationModal: 'showReservationModal',
        getModalObject: 'getModalObject',
        getLotsTableData: 'getTableParams',
        updateLotsTablePage: 'updateLotsTablePage'
      }),
      cleanPageData() {
        this.routeFrom = null;
        this.previousUuid = null;
        this.previousPage = null;
        this.nextUuid = null;
        this.nextPage = null;
      },
      onEditClick() {
        this.showReservationModal({
          sale: this.reservation.auction
        });
        this.getModalObject({uuid: this.reservation.uuid, source: SOURCE_RESERVATION_DASHBOARD});
      },
      async manualLotNoChange($event) {
        let response = await this.getReservationByLotNo({
          lotNo: $event.target.value.toUpperCase(),
          auctionUuid: !this.enableContinuousAuction ? this.reservation.auction_uuid : null
        });
        if (response.success) {
          if (response.body.results.length > 0) {
            this.cleanPageData()
            await this.$router.push({
              path: `/reservations/view/${this.reservation.uuid}`
            });
            this.initBreadcrumbs();
          } else {
            this.$errorDialog(this.$i18n.t('Lot no not found'));
          }
        } else if (response.toastError) {
          this.$errorDialog(response.toastError);
        }
      },
      onFocus() {
        this.isLotNoInputFocused = true;
      },
      initBreadcrumbs() {
        let breadcrumbs;
        if (!this.enableContinuousAuction) {
          breadcrumbs = [
            {
              text: this.$i18n.t('Sales'),
              to: {name: 'sales'},
              icon: 'server'
            },
            {
              text: this.reservation.auction.sale_no,
              to: {
                name: 'sales.dashboard',
                params: {id: this.reservation.auction_uuid}
              }
            },
            {
              text: this.$i18n.t('Lots'),
              to: {
                name: 'sales.lots',
                params: {id: this.reservation.auction_uuid}
              }
            }
          ]
        } else {
          breadcrumbs = [
            {
              text: this.$i18n.t('Lots'),
              to: {name: 'lots'},
              icon: 'server'
            }
          ]
        }
        breadcrumbs.push(
            {
              text: `${this.$options.filters.truncate(this.reservation.inventory.short_description, 50)}`
            }
        );

        this.breadcrumbs = breadcrumbs;
      },
      async onLotInputKeyUp($event) {
        if (this.loading || $event.target.value === this.reservation.lot_no || !$event.target.value) {
          return;
        }
        $event.target.value = $event.target.value.toUpperCase();
        $event.preventDefault();
        if ($event.keyCode === 13) {
          await this.manualLotNoChange($event);
        }
      },
      async onLotInputBlur($event) {
        if (this.loading || $event.target.value === this.reservation.lot_no || !$event.target.value) {
          return;
        }
        await this.manualLotNoChange($event);
      },
      async getPrevious() {
        if (['sales.lots', 'lots'].includes(this.routeFrom.name)) {
          this.updateLotsTablePage(this.previousPage)
        }

        this.$router.push({
          name: this.$route.name,
          params: {id: this.previousUuid},
        });
      },
      async getNext() {
        if (['sales.lots', 'lots'].includes(this.routeFrom.name)) {
          this.updateLotsTablePage(this.nextPage)
        }

        this.$router.push({
          name: this.$route.name,
          params: {id: this.nextUuid},
        });
      },
      leftOrRightArrowPress(evt) {
        if (this.loading) {
          return;
        }
        //39 - right arrow | 37 - left array
        if (evt.which === 37) {
          this.getPrevious(this.reservation.previous_uuid)
        } else if (evt.which === 39) {
          this.getNext(this.reservation.next_uuid)
        }
      },
      async calculateInfo() {
        if (this.routeFrom && ['sales.lots', 'lots'].includes(this.routeFrom.name)) {
          let searchParams;

          if (['sales.lots', 'lots'].includes(this.routeFrom.name)) {
            searchParams = await this.getLotsTableData();
          }

          let {
            success,
            body
          } = await InventoryService.getReservationInfo({reservation_uuid: this.reservation.uuid, ...searchParams});
          if (success) {
            this.previousUuid = body.previous;
            this.previousPage = body.previous_page;
            this.nextUuid = body.next;
            this.nextPage = body.next_page;
          }
        }
      },
    },
    async beforeMount() {
      const response = await this.getReservation(this.$route.params.id);
      if (!response.success) {
        await this.$errorDialog(this.$i18n.t('Something went wrong'));
        this.$generalGoBack(this)
      } else {
        this.initBreadcrumbs();
        this.$nextTick(async () => this.calculateInfo());
      }
    },
    beforeRouteEnter(to, from, next) {
      next(vm => {
        if (from && from.name) {
          vm.routeFrom = from;
        }
      })
    },
  }
</script>

<style lang="scss" scoped>
  @import "../scss/variables";

  .reservation-view {
    @include view-page-wrapper;
  }

  .lot-no-input {
    width: 50px
  }
</style>
